import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material'
import { CheckCircle, XCircle } from 'lucide-react'

interface VerificationConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isCurrentlyVerified: boolean
  user: {
    name?: string
    email?: string
  }
  isLoading?: boolean
}

export const VerificationConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  isCurrentlyVerified,
  user,
  isLoading,
}: VerificationConfirmDialogProps) => {
  const isApproving = !isCurrentlyVerified

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            flexShrink: 0,
            bgcolor: isApproving ? 'success.lighter' : 'error.lighter',
            color: isApproving ? 'success.main' : 'error.main',
          }}
        >
          {isApproving
            ? <CheckCircle size={20} />
            : <XCircle size={20} />
          }
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={500}>
            {isApproving ? 'Approve recruiter' : 'Disapprove recruiter'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {isApproving
              ? "This will mark the recruiter's company profile as verified. They'll gain full access to verified recruiter features."
              : "This will mark the recruiter's company profile as unverified. They'll lose access to verified recruiter features."
            }
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Box
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            fontSize: 13,
          }}
        >
          <Typography component="span" variant="body2" fontWeight={500}>
            {user.name}
          </Typography>
          {user.name && user.email && (
            <Typography component="span" variant="body2" color="text.secondary">
              {' · '}
            </Typography>
          )}
          <Typography component="span" variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isApproving ? 'success' : 'error'}
          onClick={onConfirm}
          loading={isLoading}
        >
          {isApproving ? 'Approve' : 'Disapprove'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}